import * as React from 'react'

import {
  loadActiveAgentConversation,
  openTab,
  useAppDispatch,
  useAppSelector,
  useAppStore,
  type ConversationRun
} from '@axilon-apps/domains-ontology-ui-state'

export interface PersistedConversation {
  /** `null` until the first hydration lands (or when the tab was never opened). */
  turns: ConversationRun[] | null
  loadError: string | null
}

/**
 * Hydrate a persisted conversation through the orchestrator slice (hidden
 * tab), so a transcript already loaded in an agent tab is reused rather than
 * refetched. Re-hydrates whenever `refreshKey` changes — pass a polled job's
 * `updatedAt` to follow a run that is still writing turns.
 */
export function usePersistedConversation(
  conversationId: string,
  refreshKey: string | number | undefined,
  loadErrorText: string
): PersistedConversation {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const tab = useAppSelector((s) => s.agentOrchestrator.tabs[conversationId] ?? null)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const hydrateGenRef = React.useRef(0)

  React.useEffect(() => {
    if (!conversationId) return
    const gen = ++hydrateGenRef.current
    setLoadError(null)
    // Don't seed turns: [] — that clears a cached transcript and flickers empty.
    dispatch(openTab({ id: conversationId, hidden: true }))
    const loaded = store.getState().agentOrchestrator.tabs[conversationId]?.turnsLoaded
    if (loaded && refreshKey === undefined) return
    void dispatch(loadActiveAgentConversation(conversationId, { hidden: true })).catch((err) => {
      if (hydrateGenRef.current !== gen) return
      setLoadError(err instanceof Error ? err.message : loadErrorText)
    })
  }, [conversationId, dispatch, loadErrorText, refreshKey, store])

  return { turns: tab?.turnsLoaded ? (tab.turns ?? []) : null, loadError }
}
